import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import fr from 'facet-rest';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import { useSnackbarContext } from 'Context/SnackbarContext';
import { FDStack } from '@facet-wealth/design';
import MetaFormInline from 'Components/UI/Meta/MetaForm/Components/MetaFormInline';
import { metaOverrideSet } from 'Views/Page/Content/Templates/CapitalizeRolloverIntroStepTemplate/metaOverride';
import StepNavigator from 'Components/ComponentLibrary/Molecules/StepNavigator';
import { makeStyles, useTheme } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import CapitalizeComponent
  from 'Views/Page/Content/Templates/CapitalizeRolloverWebviewStepTemplate/Content/capitalizeComponent';
import history from 'State/history';
import useQueryParams from 'Hooks/useQueryParams';
import useComponentDidMount from 'Hooks/useComponentDidMount';
import Box from '../../../../../Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import Content from './Content';

const useStyles = makeStyles(theme => ({
  stepNavigator: {
    position: 'sticky',
    zIndex: 100,
    bottom: 0,
  },
  form: {
    flexGrow: 1,
    backgroundColor: theme.palette.facet.lightGray,
  },
}));

const SHOW_CAPITALIZE_QUERY_PARAM = 'showCapitalize';

const CapitalizeRolloverWebviewStepTemplate = ({ resource }) => {
  const { loadPageContentLink } = usePageContentContext();
  // const history = useHistory();
  const [queryParams, setQuery] = useQueryParams();

  const { displayErrorMessage } = useSnackbarContext();
  const theme = useTheme();
  const classes = useStyles(theme);
  const transactionIdRef = React.useRef(null);
  const [openIframe, setOpenIframe] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);
  const [rolloverSubmitted, setRolloverSubmitted] = React.useState(false);

  const storeCapitalizeEvent = (event, value) => {
    resource.withRelationship(fr.relationships.capitalizeEvent.name,
      async (rel) => {
        const collection = await rel.link('related').get();
        const newRes = collection.create();
        newRes.attr('eventType', event);
        newRes.attr('transactionId', value);
        await newRes.submit();
      },
      () => {
        throw new Error('CapitalizeRolloverWebviewStepTemplate: storeCapitalizeEvent: no capitalizeEvent relationship found');
      });
  };

  const handleCapitalizeExit = useCallback(() => {
    if (!rolloverSubmitted) {
      queryParams.set(SHOW_CAPITALIZE_QUERY_PARAM, 'false');
      setQuery(queryParams);
      return;
    }
    storeCapitalizeEvent('onCapitalizeExit', transactionIdRef.current);
    resource.withRelationship(fr.relationships.capitalizeExit.name,
      (rel) => {
        loadPageContentLink(rel.link('related'));
        queryParams.set(SHOW_CAPITALIZE_QUERY_PARAM, 'false');
        setQuery(queryParams);
      },
      () => {
        history.push('/');
      });
  }, [queryParams, rolloverSubmitted]);

  const handleCapitalizeRolloverEvent = async (rel) => {
    const collection = await rel.link('related').get();
    const newRes = collection.create();
    newRes.attr('capitalizeTransactionId', transactionIdRef.current);
    try {
      await newRes.submit();
    } catch (e) {
      displayErrorMessage('Failed to submit capitalize rollover');
    }
  };

  const handleCapitalizeRolloverSubmitted = async () => {
    storeCapitalizeEvent('onCapitalizeRolloverSubmitted', transactionIdRef.current);
    await resource.withRelationship(fr.relationships.capitalizeRolloverSubmitted.name,
      handleCapitalizeRolloverEvent,
      () => {
        throw new Error('CapitalizeRolloverWebviewStepTemplate: handleCapitalizeRolloverSubmitted: no capitalizeRolloverSubmitted relationship found');
      });
  };

  const handleCapitalize401kStepComplete = async (event) => {
    setRolloverSubmitted(true);
    storeCapitalizeEvent('onCapitalize401kStepComplete', event?.data?.onCapitalize401kStepComplete);

    // the rollover submitted event that comes after this doesnt have the transactionId.
    // so we store it in a ref to use later
    transactionIdRef.current = event?.data?.onCapitalize401kStepComplete;

    await resource.withRelationship(fr.relationships.capitalizeRolloverCreated.name,
      handleCapitalizeRolloverEvent,
      () => {
        throw new Error('CapitalizeRolloverWebviewStepTemplate: handleCapitalize401kStepComplete: no capitalizeRolloverCreated relationship found');
      });
  };

  const handleCapitalizeRolloverCancelled = async (event) => {
    storeCapitalizeEvent('onCapitalizeRolloverCanceled', event?.data?.onCapitalizeRolloverCanceled);
    await resource.withRelationship(fr.relationships.capitalizeRolloverCancelled.name,
      async (rel) => {
        const collection = await rel.link('related').get();
        const newRes = collection.create();
        newRes.attr('capitalizeTransactionId', event?.data?.onCapitalizeRolloverCanceled);
        try {
          await newRes.submit();
        } catch (e) {
          displayErrorMessage('Failed to handle capitalize rollover cancel');
        }
      },
      () => {
        throw new Error('CapitalizeRolloverWebviewStepTemplate: handleCapitalizeRolloverCancelled: no capitalizeRolloverCancelled relationship found');
      });
  };

  const handleEvent = useCallback(async (event) => {
    if (event?.data?.onCapitalizeExit) {
      await handleCapitalizeExit(event);
    }

    // fires when webview creates the transaction. provides the transactionId
    if (event?.data?.onCapitalize401kStepComplete) {
      await handleCapitalize401kStepComplete(event);
    }

    // fires when user submits the rollover
    if (event?.data?.onCapitalizeRolloverSubmitted) {
      await handleCapitalizeRolloverSubmitted(event);
    }

    // fires when user cancels the rollover
    if (event?.data?.onCapitalizeRolloverCanceled) {
      await handleCapitalizeRolloverCancelled(event);
    }

    if (event?.data?.onCreateTradIra) {
      storeCapitalizeEvent('onCreateTradIra', event?.data?.onCreateTradIra);
    }

    if (event?.data?.onCreateRothIra) {
      storeCapitalizeEvent('onCreateRothIra', event?.data?.onCreateRothIra);
    }

    // capitalize says we wouldnt get this, but logging it just in case
    if (event?.data?.onError) {
      storeCapitalizeEvent('onError', event?.data?.onError);
    }
  }, [handleCapitalizeExit]);

  React.useEffect(() => {
    window.addEventListener('message', handleEvent);

    return () => window.removeEventListener('message', handleEvent);
  }, [handleEvent]);

  React.useEffect(() => {
    if (queryParams.get(SHOW_CAPITALIZE_QUERY_PARAM) !== 'true') {
      setOpenIframe(false);
      return;
    }

    setOpenIframe(true);
  }, [queryParams]);

  useComponentDidMount(() => {}, () => {
    queryParams.delete(SHOW_CAPITALIZE_QUERY_PARAM);
    setQuery(queryParams);
  });

  const handleBack = async () => {
    resource.withRelationship(fr.relationships.previousLink.name,
      () => {
        loadPageContentLink(resource.relationship(fr.relationships.previousLink.name).link('related'));
      },
      () => null);
  };

  const handleNext = async () => {
    const isManualMode = resource.withRelationship(fr.relationships.webview.name, () => false, () => true);

    // if manual mode, then we just need to submit the step to get the next step
    if (isManualMode) {
      setSubmitted(true);
      const [resp] = await resource.submit();
      loadPageContentLink(resp.link('self'));
      return;
    }

    // if webview mode, then we need to open the webview in an iframe
    queryParams.set(SHOW_CAPITALIZE_QUERY_PARAM, 'true');
    setQuery(queryParams);
  };

  return (
    <FDStack direction={'column'} minHeight={'100dvh'}>
      {openIframe && <CapitalizeComponent rel={resource.relationship(fr.relationships.webview.name)} />}
      {!openIframe && (
        <>
          <MetaFormInline
            className={classes.form}
            noIndividualSubmitButtons
            resource={resource}
            layout={Content}
            LayoutProps={{
              resource,
            }}
            metaOverrideSet={metaOverrideSet}
          />

          <Box className={classes.stepNavigator}>
            <StepNavigator
              onNext={handleNext}
              isLoading={submitted}
              disabled={submitted}
              onPrevious={handleBack}
            >
              {resource.attr('primaryActionLabel') || 'Continue'}
            </StepNavigator>
          </Box>
        </>
      )}
    </FDStack>
  );
};

CapitalizeRolloverWebviewStepTemplate.defaultProps = {
};

CapitalizeRolloverWebviewStepTemplate.propTypes = {
  resource: PropTypes.instanceOf(fr.Resource).isRequired,
};

export default CapitalizeRolloverWebviewStepTemplate;
